import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import { TASK_COLLECTION_STALE_TIME } from './taskQueryConfig';
import type { TimelineDto } from '../types';

interface UseTimelineDataParams {
  projectId: string;
  boardId: string;
  dateStart: string;
  dateEnd: string;
  /** Distinct assignee IDs from the tasks already fetched for this board — see `useTimelineTasks`. */
  userIds: string[];
}

interface UseTimelineDataResult {
  milestones: TimelineDto['milestones'];
  absences: TimelineDto['absences'];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches milestones + the vacation overlay for the visible timeline window.
 * Must be called with the board's *current* assignee IDs (from
 * `useTimelineTasks`) — the backend silently narrows `userIds` to real board
 * assignees regardless of what's sent, so a stale list just returns less data,
 * not an error.
 */
export function useTimelineData({
  projectId,
  boardId,
  dateStart,
  dateEnd,
  userIds,
}: UseTimelineDataParams): UseTimelineDataResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const { data, isLoading, isError } = useQuery({
    queryKey: TASK_KEYS.timelineData(orgId ?? '', boardId, dateStart, dateEnd, userIds),
    queryFn: ({ signal }) =>
      tasksService.getTimeline(
        projectId,
        boardId,
        { dateStart, dateEnd, userIds: userIds.join(',') },
        signal,
      ),
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId && userIds.length > 0,
    // Scrolling widens the range, which changes the key — without this the query
    // would drop to `isLoading` on every extension, unmounting the grid, wiping
    // the scroll position, and stranding the user at the left edge in a loop.
    placeholderData: keepPreviousData,
  });

  return {
    milestones: data?.milestones ?? [],
    absences: data?.absences ?? [],
    isLoading: userIds.length > 0 && isLoading,
    isError,
  };
}
